"use client";

import { useContext, useState, useEffect } from "react";
import { FiChevronRight } from "react-icons/fi";
import { usePathname, useRouter } from "next/navigation";

import { Button } from "@/components/Button";
import { Stepper } from "@/components/Stepper";
import { Wrapper } from "@/components/Wrapper";
import { ModalQRPreview } from "@/components/ModalQRPreview";

import { UserContext } from "@/contexts/UserContext";
import { QREditorContext } from "@/contexts/QREditorContext";
import { GlobalContext } from "@/contexts/GlobalContext";

import { useToast } from "@/hooks/useToast";
import { useQR, useQRStyle } from "@/hooks/useQR";

import { getTokenData } from "@/services/CookiesService";
import { amplitudeTrackEvent } from "@/services/AmplitudeService/AmplitudeService";

import { clearPendingActionCookie } from "@/actions/clearPendingActionCookie";
import { setPendingActionCookie } from "@/actions/setPendingActionCookie";
import { getPendingActionCookie } from "@/actions/getPendingActionCookie";

import { ActionTypes } from "@/internals/constants/actionTypes";
import { AMPLITUDE_EVENT_NAME } from "@/internals/constants/events";

import { omittedValues, getQRCodeUrl } from "@/utils/qrEditorHelper";
import { QR_PAGE_INDEX } from "@/utils/qrEditorNavigatorHelper";
import {
  getPageIndexFromUrl,
  handleQRFooterDirectNavigation,
  handleBackButton,
  handleNextButton,
} from "@/utils/qrEditorNavigatorHelper";
import { paths } from "@/utils/paths";

import { initialStepperButtons } from "@/templates/QrEditorTemplates/qr-layout/qr-layout";

import { QREditorLayoutTemplateHelperButtonDataProps } from "@/templates/QrEditorTemplates/qr-layout/types";
import { OnNextButtonAction, OnPreviewModalAction } from "@/types/pendingActions";
import { QREditorContextProps } from "@/contexts/QREditorContext/types";
import { StepperHelperButtonProps } from "@/components/Stepper/types";
import { QRFooterProps } from "./types";

import "./styles.scss";
import { AmplitudeContext } from "@/contexts/AmplitudeContext/AmplitudeContext";
import { getSiteInfo } from "@/sites";
import { SiteName } from "@/sites/types";

const initialHelperButtons = (helperButtonsData: QREditorLayoutTemplateHelperButtonDataProps) => {
  return {
    nextButton: { label: helperButtonsData.nextButton.label },
    backButton: { label: helperButtonsData.cancelButton.label },
  };
};

export const QREditorFooter = ({ data }: QRFooterProps) => {
  const { siteName } = getSiteInfo(process.env.NEXT_PUBLIC_DOMAIN!) || {};

  const router = useRouter();
  const pathname = usePathname();

  const { toast } = useToast();

  const globalContext = useContext(GlobalContext);
  const userContext = useContext(UserContext);
  const { setSource } = useContext(AmplitudeContext);
  const qrEditorContext = useContext<QREditorContextProps<any>>(QREditorContext);

  const { requestQR, isLoading, newData } = useQR();
  const { getRawData, updateQRCode } = useQRStyle();

  const stepperButtons = initialStepperButtons(data.stepperButtons);

  const [previewType, setPreviewType] = useState<"qr" | "data" | undefined>(
    qrEditorContext?.qrData?.currentPageIndex === QR_PAGE_INDEX.DESIGN_QR ? "qr" : "data"
  );

  const [isQRActive, setisQRActive] = useState(false);
  const [stepperHelperButtons] = useState<StepperHelperButtonProps>(
    initialHelperButtons(data.helperButtons)
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [qrRaw, setQrRaw] = useState(undefined);
  const [isQRInit, setIsQRInit] = useState(
    qrEditorContext?.qrData?.qrCodeReference === undefined ? true : false
  );

  const onPreviewQr = () => {
    updateQRCode({
      qrStyle: qrEditorContext.qrData.qrStyle,
      qrUrl: getQRCodeUrl(qrEditorContext.qrData, true),
    });

    if (requestQR) {
      qrEditorContext?.setIsQrLoading(true);
      requestQR()
        .then(async (data: any) => {
          if (data !== undefined) {
            qrEditorContext?.setQrData(
              {
                ...qrEditorContext.qrData,
                qrCodeId: data?.id || qrEditorContext.qrData.qrCodeId,
                qrCodeReference: data?.reference || qrEditorContext.qrData.qrCodeReference,
              },
              [...omittedValues(qrEditorContext?.qrData.data)]
            );
            const raw = await getRawData();

            setIsModalOpen(true);
            setQrRaw(raw);
            qrEditorContext?.setIsQrLoading(false);
          }
        })
        .catch(() => {
          toast({ message: "genericError", type: "error", marginBottom: 80 });
        });
    }
  };

  const isNextButtonVisible = () => {
    const currentPageIndex = qrEditorContext?.qrData.currentPageIndex;
    return (
      currentPageIndex > QR_PAGE_INDEX.SELECT_QR_TYPE && currentPageIndex < QR_PAGE_INDEX.REGISTER
    );
  };

  const handlePreviewModal = () => {
    if (previewType === "qr") {
      onPreviewQr();
    } else {
      const tokenData = getTokenData();
      if (tokenData?.isTokenExpired) {
        const currentPageIndex = qrEditorContext?.qrData.currentPageIndex;
        const qrType = qrEditorContext?.qrData.type;

        const isDesignPage = currentPageIndex === QR_PAGE_INDEX.DESIGN_QR ? "design" : undefined;

        const pendingAction: OnPreviewModalAction = {
          type: ActionTypes.PREVIEW_MODAL,
          redirectTo: paths.qrEditor(qrType, isDesignPage),
        };
        setPendingActionCookie(pendingAction);
        router.push(paths.signup());
      } else {
        setIsModalOpen(true);
      }
    }
  };

  const onNextButton = () => {
    const tokenData = getTokenData();

    if (tokenData?.isTokenExpired) {
      const currentPageIndex = qrEditorContext?.qrData.currentPageIndex;
      const qrType = qrEditorContext?.qrData.type;

      const isDesignPage = currentPageIndex === QR_PAGE_INDEX.DESIGN_QR ? "design" : undefined;

      const pendingAction: OnNextButtonAction = {
        type: ActionTypes.ON_NEXT_BUTTON,
        redirectTo: paths.qrEditor(qrType, isDesignPage),
      };
      setPendingActionCookie(pendingAction);
      router.push(paths.login());
    } else {
      handleNextButton(
        globalContext,
        qrEditorContext,
        userContext,
        requestQR,
        toast,
        true,
        router,
        setSource
      );
    }
  };

  useEffect(() => {
    const parsedPendingAction = getPendingActionCookie();

    parsedPendingAction.then((pendingAction) => {
      if (pendingAction) {
        if (pendingAction.type === ActionTypes.ON_NEXT_BUTTON) {
          onNextButton();
          clearPendingActionCookie();
        } else if (pendingAction.type === ActionTypes.PREVIEW_MODAL) {
          handlePreviewModal();
          clearPendingActionCookie();
        }
      }
    });
  }, []);

  useEffect(() => {
    if (Object.keys(qrEditorContext?.qrData.data).length > 2) setIsQRInit(false);
  }, [qrEditorContext?.qrData.data]);

  // Esta función controlara la navegación directa
  useEffect(() => {
    const currentPageIndex = getPageIndexFromUrl(pathname);
    const contextPageIndex = qrEditorContext?.qrData?.currentPageIndex;
    qrEditorContext.setQrData({ ...qrEditorContext.qrData, currentPageIndex });

    // Esta función controlara la navegación directa
    return handleQRFooterDirectNavigation(
      currentPageIndex,
      contextPageIndex,
      qrEditorContext,
      router
    );
  }, [pathname, globalContext]);

  useEffect(() => {
    //QR is active if form methods are true and there's no extra validations errors in the form
    const activeQR = qrEditorContext?.qrData.isValidForm && !qrEditorContext?.validationError;
    setisQRActive(activeQR);
  }, [qrEditorContext]);

  useEffect(() => {
    if (newData) {
      qrEditorContext.setQrData(
        {
          ...qrEditorContext.qrData,
          data: newData.data,
          qrStyle: newData.qrStyle,
        },
        [...omittedValues(qrEditorContext?.qrData.data)]
      );
    }
  }, [newData]);

  return (
    <footer
      data-testid="qr-footer"
      className="qr-footer">
      <Wrapper customClass="qr-footer__wrapper">
        <Button
          onClick={() => handleBackButton(qrEditorContext, router)}
          tertiary
          dataQA="qr-editor-previous-step-button"
          label={
            qrEditorContext?.qrData?.currentPageIndex === QR_PAGE_INDEX.SELECT_QR_TYPE
              ? data.helperButtons.cancelButton.label
              : data.helperButtons.backButton.label
          }
          className="helper-button"
          rounded={siteName === SiteName.QRNOW}
        />
        {qrEditorContext?.qrData.currentPageIndex > 0 && (
          <div className="qr-footer__show-buttons">
            <Button
              type="button"
              dataQA="qr-editor-preview-button"
              tertiary
              label="Preview"
              onClick={() => handlePreviewModal()}
              customClass="qrFooter-preview-button"
              rounded={siteName === SiteName.QRNOW}
            />
          </div>
        )}
        <Stepper
          stepButtons={stepperButtons}
          isValidForm={qrEditorContext?.qrData?.isValidForm}
        />

        {isNextButtonVisible() ? (
          <Button
            onClick={() => onNextButton()}
            dataQA="qr-editor-next-step-button"
            label={
              qrEditorContext?.qrData?.currentPageIndex === QR_PAGE_INDEX.DESIGN_QR
                ? data.helperButtons.finishButton.label
                : data.helperButtons.nextButton.label
            }
            icon={<FiChevronRight />}
            loading={isLoading && !qrEditorContext?.isQrLoading}
            disabled={!qrEditorContext?.qrData.type}
            className={`helper-button${
              stepperHelperButtons.nextButton.hidden ? "--hidden" : ""
            } next`}
            rounded={siteName === SiteName.QRNOW}
          />
        ) : (
          <div className="button--helper-button"></div>
        )}
        {isModalOpen && (
          <ModalQRPreview
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            previewType={previewType}
            data={{
              qrData: qrEditorContext.qrData as any,
              previewTranslations: data.previewTranslations,
              previewType,
              setPreviewType,
              isQRActive,
              raw: qrRaw,
              handlePreviewModal,
            }}
            setQrRaw={setQrRaw}
            isQRInit={isQRInit}
            modalData={data.modalData}
          />
        )}
      </Wrapper>
    </footer>
  );
};
